import React, { useState, useMemo } from 'react';
import { BarChart3, Image, PenSquare, Home, Settings, Hotel, CalendarRange, Users, PanelRight, Lightbulb, LogOut, Bot, TrendingUp, LineChart, Star, Workflow, DollarSign, Heart, Users2, Megaphone, Leaf, ShieldCheck, Utensils, Clipboard, Grid3X3, Brain, Database, Sparkles, Building2, Trash2, Cog, Calendar, ChevronDown, ChevronRight, UserCheck, Wrench, Calculator, Target, CreditCard, MessageCircle } from 'lucide-react';
import { FileText } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { useAuth } from '@/contexts/AuthContext';
import T from '@/components/T';
import sidebarTranslations from '@/i18n/locales/components/sidebar';
import { CacheResetButton } from '@/components/cache/CacheResetButton';
import { HotelSelectionModal } from '@/components/dashboard/modals/HotelSelectionModal';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { SubscriptionStatusBadge } from '@/components/subscription/SubscriptionStatusBadge';
import { Logo } from '@/components/ui/Logo';

interface SidebarProps {
  collapsed: boolean;
}

interface SidebarItemProps {
  icon: React.ElementType;
  label: React.ReactNode;
  to: string;
  collapsed: boolean;
}

// Маппинг путей к секциям permissions
const pathToPermissionSection: Record<string, string> = {
  '/admin/room-grid': 'dashboard',
  '/admin/ai-room-grid': 'dashboard',
  '/admin/bookings-list': 'bookings',
  '/admin/meals': 'services',
  '/admin/rooms': 'rooms',
  '/admin/dashboard': 'dashboard',
  '/admin/ai-revenue-manager': 'ai',
  '/admin/market-intelligence': 'analytics',
  '/admin/analytics': 'analytics',
  '/admin/ai-strategic': 'ai',
  '/admin/ai-market': 'ai',
  '/admin/ai-finance': 'finance',
  '/admin/ai-marketing': 'marketing',
  '/admin/ai-operations': 'ai',
  '/admin/ai-reputation': 'ai',
  '/admin/ai-guest': 'guests',
  '/admin/ai-fnb': 'services',
  '/admin/ai-hr': 'users',
  '/admin/ai-sustainability': 'ai',
  '/admin/ai-security': 'settings',
  '/admin/mice': 'bookings',
  '/admin/onboarding': 'settings',
  '/admin/settings': 'settings',
  '/admin/config': 'settings',
  '/admin/restrictions-calendar': 'rates',
  '/admin/guest-management': 'guests',
  '/admin/users': 'users',
  '/admin/ai-assistant': 'ai',
  '/admin/bots': 'ai',
  '/admin/bot': 'ai',
  '/admin/deleted-bookings': 'bookings',
  '/admin/migration': 'settings',
  '/admin/offers': 'marketing',
  '/admin/channel-manager': 'rates',
  '/admin/content': 'marketing',
  '/admin/media': 'marketing',
  '/subscription/payment': 'payments',
  '/admin/hotel-switcher': 'settings',
};

const SidebarItem = ({
  icon: Icon,
  label,
  to,
  collapsed
}: SidebarItemProps) => {
  return (
    <NavLink 
      to={to} 
      className={({ isActive }) => 
        `sidebar-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`
      }
    >
      <Icon className="h-5 w-5" />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
};

// Компонент с проверкой permissions
const PermissionSidebarItem = ({
  icon,
  label,
  to,
  collapsed,
  hasPermission,
  isSuperAdmin
}: SidebarItemProps & { hasPermission: (section: string, action?: string) => boolean; isSuperAdmin: boolean }) => {
  const requiredSection = pathToPermissionSection[to];
  
  // Super admin всегда видит все
  if (isSuperAdmin) {
    return <SidebarItem icon={icon} label={label} to={to} collapsed={collapsed} />;
  }
  
  // Если секция не определена, показываем пункт всем
  if (!requiredSection) {
    return <SidebarItem icon={icon} label={label} to={to} collapsed={collapsed} />;
  }
  
  // Проверяем permissions
  if (!hasPermission(requiredSection, 'view')) {
    return null;
  }
  
  return <SidebarItem icon={icon} label={label} to={to} collapsed={collapsed} />;
};

export const Sidebar = ({ collapsed }: SidebarProps) => {
  const { isSuperAdmin } = useSuperAdmin();
  const { signOut, hasPermission, systemRole } = useAuth();
  const [isAIDirectorsOpen, setIsAIDirectorsOpen] = useState(false);
  const [isAllSettingsOpen, setIsAllSettingsOpen] = useState(false);
  const [hotelModalOpen, setHotelModalOpen] = useState(false);
  const { currentHotel, availableHotels } = useOptimizedHotel();

  // Проверка является ли пользователь super_admin (любой вариант)
  const isFullAdmin = useMemo(() => {
    return isSuperAdmin || systemRole === 'super_admin' || systemRole === 'platform_owner';
  }, [isSuperAdmin, systemRole]);

  // Хелпер для проверки доступа к секции
  const canAccess = (section: string): boolean => {
    if (isFullAdmin) return true;
    return hasPermission(section, 'view');
  };

  const CollapsibleSection = ({
    title,
    icon: Icon,
    isOpen,
    onToggle,
    children,
    requiredSection
  }: {
    title: React.ReactNode;
    icon: React.ElementType;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    requiredSection?: string;
  }) => {
    // Если нужна проверка и нет доступа - не показываем секцию
    if (requiredSection && !canAccess(requiredSection) && !isFullAdmin) {
      // Проверяем есть ли хоть один видимый child
      const childrenArray = React.Children.toArray(children);
      const hasVisibleChildren = childrenArray.some(child => child !== null);
      if (!hasVisibleChildren) return null;
    }
    
    if (collapsed) {
      return (
        <div className="space-y-1">
          <div className="sidebar-item justify-center px-2 cursor-pointer" onClick={onToggle}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      );
    }
    
    return (
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger className="sidebar-item w-full justify-between hover:bg-sidebar-accent/50">
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5" />
            <span>{title}</span>
          </div>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1 mt-1">
          <div className="ml-6 space-y-1">
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <aside className={`bg-sidebar flex flex-col h-screen ${collapsed ? 'w-16' : 'w-64'} transition-all duration-300 border-r border-sidebar-border`}>
      <div className="flex items-center justify-center h-16 border-b border-sidebar-border">
        <Logo size="lg" showText={!collapsed} />
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto py-4 px-3">
        {/* 1. Номера и тарифы — ПЕРВОЕ МЕСТО */}
        {canAccess('rooms') && (
          <>
            <div className="space-y-1">
              <SidebarItem icon={Hotel} label={<T ru={sidebarTranslations.mainSections.roomsAndRates.ru} en={sidebarTranslations.mainSections.roomsAndRates.en} />} to="/admin/rooms" collapsed={collapsed} />
            </div>
            <Separator className="my-4 bg-sidebar-border/50" />
          </>
        )}

        {/* 2. AI-Аналитик шахматки */}
        {canAccess('dashboard') && (
          <>
            <div className="space-y-1">
              <SidebarItem icon={Brain} label={<T ru={sidebarTranslations.roomGrid.aiRoomGrid.ru} en={sidebarTranslations.roomGrid.aiRoomGrid.en} />} to="/admin/ai-room-grid" collapsed={collapsed} />
            </div>
            <Separator className="my-4 bg-sidebar-border/50" />
          </>
        )}

        {/* 3. Аналитика (вынесена из AI Помощников) */}
        {canAccess('analytics') && (
          <>
            <div className="space-y-1">
              <SidebarItem icon={BarChart3} label={<T ru={sidebarTranslations.main.analytics.ru} en={sidebarTranslations.main.analytics.en} />} to="/admin/analytics" collapsed={collapsed} />
            </div>
            <Separator className="my-4 bg-sidebar-border/50" />
          </>
        )}

        {/* 4. Ассистент */}
        {canAccess('dashboard') && (
          <>
            <div className="space-y-1">
              <SidebarItem icon={Home} label={<T ru={sidebarTranslations.main.assistant.ru} en={sidebarTranslations.main.assistant.en} />} to="/admin/dashboard" collapsed={collapsed} />
            </div>
            <Separator className="my-4 bg-sidebar-border/50" />
          </>
        )}

        {/* AI Директора - требует доступ к AI */}
        {canAccess('ai') && (
          <>
            <div className="space-y-1">
              <CollapsibleSection 
                title={<T ru={sidebarTranslations.aiDirectors.title.ru} en={sidebarTranslations.aiDirectors.title.en} />} 
                icon={UserCheck} 
                isOpen={isAIDirectorsOpen} 
                onToggle={() => setIsAIDirectorsOpen(!isAIDirectorsOpen)}
              >
                {/* Revenue секция */}
                <SidebarItem icon={Sparkles} label={<T ru={sidebarTranslations.main.revenueAiAutopilot.ru} en={sidebarTranslations.main.revenueAiAutopilot.en} />} to="/admin/ai-revenue-manager" collapsed={false} />
                <SidebarItem icon={Target} label={<T ru={sidebarTranslations.main.marketIntelligence.ru} en={sidebarTranslations.main.marketIntelligence.en} />} to="/admin/market-intelligence" collapsed={false} />
                {/* AI Директора */}
                <SidebarItem icon={TrendingUp} label={<T ru={sidebarTranslations.aiDirectors.strategicAdvisor.ru} en={sidebarTranslations.aiDirectors.strategicAdvisor.en} />} to="/admin/ai-strategic" collapsed={false} />
                <SidebarItem icon={LineChart} label={<T ru={sidebarTranslations.aiDirectors.marketAnalyst.ru} en={sidebarTranslations.aiDirectors.marketAnalyst.en} />} to="/admin/ai-market" collapsed={false} />
                {canAccess('finance') && (
                  <SidebarItem icon={DollarSign} label={<T ru={sidebarTranslations.aiDirectors.financialCenter.ru} en={sidebarTranslations.aiDirectors.financialCenter.en} />} to="/admin/ai-finance" collapsed={false} />
                )}
                {canAccess('marketing') && (
                  <SidebarItem icon={Megaphone} label={<T ru={sidebarTranslations.aiDirectors.marketingConsultant.ru} en={sidebarTranslations.aiDirectors.marketingConsultant.en} />} to="/admin/ai-marketing" collapsed={false} />
                )}
                <SidebarItem icon={Workflow} label={<T ru={sidebarTranslations.aiDirectors.operationsOptimizer.ru} en={sidebarTranslations.aiDirectors.operationsOptimizer.en} />} to="/admin/ai-operations" collapsed={false} />
                <SidebarItem icon={Star} label={<T ru={sidebarTranslations.aiDirectors.reputationCenter.ru} en={sidebarTranslations.aiDirectors.reputationCenter.en} />} to="/admin/ai-reputation" collapsed={false} />
                {canAccess('guests') && (
                  <SidebarItem icon={Heart} label={<T ru={sidebarTranslations.aiDirectors.guestExperience.ru} en={sidebarTranslations.aiDirectors.guestExperience.en} />} to="/admin/ai-guest" collapsed={false} />
                )}
                {canAccess('services') && (
                  <SidebarItem icon={Utensils} label={<T ru={sidebarTranslations.aiDirectors.fnbAnalyst.ru} en={sidebarTranslations.aiDirectors.fnbAnalyst.en} />} to="/admin/ai-fnb" collapsed={false} />
                )}
                {canAccess('users') && (
                  <SidebarItem icon={Users2} label={<T ru={sidebarTranslations.aiDirectors.hrStrategist.ru} en={sidebarTranslations.aiDirectors.hrStrategist.en} />} to="/admin/ai-hr" collapsed={false} />
                )}
                <SidebarItem icon={Leaf} label={<T ru={sidebarTranslations.aiDirectors.sustainabilityExpert.ru} en={sidebarTranslations.aiDirectors.sustainabilityExpert.en} />} to="/admin/ai-sustainability" collapsed={false} />
                {canAccess('settings') && (
                  <SidebarItem icon={ShieldCheck} label={<T ru={sidebarTranslations.aiDirectors.securitySpecialist.ru} en={sidebarTranslations.aiDirectors.securitySpecialist.en} />} to="/admin/ai-security" collapsed={false} />
                )}
              </CollapsibleSection>
            </div>
            <Separator className="my-4 bg-sidebar-border/50" />
          </>
        )}

        {/* Все настройки */}
        <div className="space-y-1">
          <CollapsibleSection 
            title={<T ru={sidebarTranslations.allSettings.title.ru} en={sidebarTranslations.allSettings.title.en} />} 
            icon={Wrench} 
            isOpen={isAllSettingsOpen} 
            onToggle={() => setIsAllSettingsOpen(!isAllSettingsOpen)}
          >
            {canAccess('bookings') && (
              <SidebarItem icon={Calculator} label={<T ru={sidebarTranslations.main.miceCalculator.ru} en={sidebarTranslations.main.miceCalculator.en} />} to="/admin/mice" collapsed={false} />
            )}
            {canAccess('settings') && (
              <>
                <SidebarItem icon={Clipboard} label={<T ru={sidebarTranslations.hotelSetup.title.ru} en={sidebarTranslations.hotelSetup.title.en} />} to="/admin/onboarding" collapsed={false} />
                <SidebarItem icon={Settings} label={<T ru={sidebarTranslations.allSettings.systemSettings.ru} en={sidebarTranslations.allSettings.systemSettings.en} />} to="/admin/settings" collapsed={false} />
                <SidebarItem icon={Cog} label={<T ru={sidebarTranslations.allSettings.universalConfigurator.ru} en={sidebarTranslations.allSettings.universalConfigurator.en} />} to="/admin/config" collapsed={false} />
              </>
            )}
            {canAccess('rates') && (
              <>
                <SidebarItem icon={Calendar} label={<T ru={sidebarTranslations.allSettings.calendarAndRates.ru} en={sidebarTranslations.allSettings.calendarAndRates.en} />} to="/admin/restrictions-calendar" collapsed={false} />
                <SidebarItem icon={Cog} label={<T ru="Интеграции" en="Integrations" />} to="/admin/integrations" collapsed={false} />
                <SidebarItem icon={PanelRight} label={<T ru={sidebarTranslations.allSettings.channelManager.ru} en={sidebarTranslations.allSettings.channelManager.en} />} to="/admin/channel-manager" collapsed={false} />
              </>
            )}
            {canAccess('guests') && (
              <SidebarItem icon={FileText} label={<T ru={sidebarTranslations.allSettings.documentsSection.ru} en={sidebarTranslations.allSettings.documentsSection.en} />} to="/admin/guest-management" collapsed={false} />
            )}
            {canAccess('users') && (
              <SidebarItem icon={Users} label={<T ru={sidebarTranslations.allSettings.users.ru} en={sidebarTranslations.allSettings.users.en} />} to="/admin/users" collapsed={false} />
            )}
            {canAccess('ai') && (
              <>
                <SidebarItem icon={Lightbulb} label={<T ru={sidebarTranslations.main.aiAssistant.ru} en={sidebarTranslations.main.aiAssistant.en} />} to="/admin/ai-assistant" collapsed={false} />
                <SidebarItem icon={Bot} label={<T ru={sidebarTranslations.allSettings.aiSpecialistsManagement.ru} en={sidebarTranslations.allSettings.aiSpecialistsManagement.en} />} to="/admin/bots" collapsed={false} />
                <SidebarItem icon={MessageCircle} label={<T ru="Telegram Бот" en="Telegram Bot" />} to="/admin/bot" collapsed={false} />
              </>
            )}
            {canAccess('bookings') && (
              <SidebarItem icon={Trash2} label={<T ru={sidebarTranslations.allSettings.deletedBookings.ru} en={sidebarTranslations.allSettings.deletedBookings.en} />} to="/admin/deleted-bookings" collapsed={false} />
            )}
            {canAccess('services') && (
              <SidebarItem icon={Utensils} label={<T ru="Питание" en="Meals" />} to="/admin/meals" collapsed={false} />
            )}
            {canAccess('services') && (
              <SidebarItem icon={DollarSign} label={<T ru="Цены питания (F&B)" en="F&B Prices" />} to="/admin/meal-prices" collapsed={false} />
            )}
            {canAccess('settings') && (
              <SidebarItem icon={Database} label={<T ru={sidebarTranslations.allSettings.dataMigration.ru} en={sidebarTranslations.allSettings.dataMigration.en} />} to="/admin/migration" collapsed={false} />
            )}
            {canAccess('marketing') && (
              <>
                <SidebarItem icon={PanelRight} label={<T ru={sidebarTranslations.allSettings.specialOffers.ru} en={sidebarTranslations.allSettings.specialOffers.en} />} to="/admin/offers" collapsed={false} />
                <SidebarItem icon={PenSquare} label={<T ru={sidebarTranslations.allSettings.hotelWebsite.ru} en={sidebarTranslations.allSettings.hotelWebsite.en} />} to="/admin/content" collapsed={false} />
                <SidebarItem icon={Image} label={<T ru={sidebarTranslations.allSettings.media.ru} en={sidebarTranslations.allSettings.media.en} />} to="/admin/media" collapsed={false} />
              </>
            )}
            {canAccess('payments') && (
              <SidebarItem icon={CreditCard} label={<T ru="Оплата подписки" en="Subscription Payment" />} to="/subscription/payment" collapsed={false} />
            )}
            {isFullAdmin && (
              <SidebarItem icon={Building2} label={<T ru={sidebarTranslations.allSettings.hotelSwitcher.ru} en={sidebarTranslations.allSettings.hotelSwitcher.en} />} to="/admin/hotel-switcher" collapsed={false} />
            )}
          </CollapsibleSection>
        </div>
      </div>

      <div className="p-3 border-t border-sidebar-border space-y-2 text-primary">
        {/* Subscription Status Badge */}
        <SubscriptionStatusBadge collapsed={collapsed} />
        
        <div className={`flex ${collapsed ? 'justify-center' : 'justify-start'}`}>
          <LanguageSwitcher variant="outline" size="sm" showFlag={true} showText={!collapsed} />
        </div>
        
        {isFullAdmin && (
          <Button 
            variant="outline" 
            size={collapsed ? "icon" : "default"} 
            className={`w-full justify-${collapsed ? "center" : "start"} border-primary/50 text-primary hover:bg-primary/10`} 
            onClick={() => setHotelModalOpen(true)} 
            disabled={availableHotels.length === 0}
          >
            <Building2 className="h-5 w-5 mr-2" />
            {!collapsed && <span>{currentHotel?.name || <T ru="Отель не выбран" en="No hotel selected" />}</span>}
          </Button>
        )}
        
        <CacheResetButton 
          variant="outline" 
          size={collapsed ? "icon" : "default"} 
          className={`w-full justify-${collapsed ? "center" : "start"} border-green-600/50 text-green-600 hover:bg-green-600/10 hover:text-green-700`}
        >
          {!collapsed && <span><T ru="Обновить приложение" en="Update App" /></span>}
        </CacheResetButton>
        
        <Button variant="ghost" className={`w-full justify-${collapsed ? "center" : "start"}`} onClick={signOut}>
          <LogOut className="h-5 w-5 mr-2" />
          {!collapsed && <span><T ru={sidebarTranslations.footer.logout.ru} en={sidebarTranslations.footer.logout.en} /></span>}
        </Button>
      </div>

      <HotelSelectionModal open={hotelModalOpen} onOpenChange={setHotelModalOpen} />
    </aside>
  );
};
